import { db, storage } from '../firebase/config.ts';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  GeoPoint,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';
import { Event } from '../types/Event';

const STORAGE_PATH = 'events_images'; // Centralized storage path

export interface EventsPageResult {
  events: Event[];
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

const isVisibleEventData = (data: DocumentData): boolean =>
  data.hidden !== true && !data.duplicateOf;

export const eventService = {
  async getEventsPage(
    pageSize: number,
    lastVisible: QueryDocumentSnapshot<DocumentData> | null = null
  ): Promise<EventsPageResult> {
    const eventsRef = collection(db, 'posts');
    // Fetch a small cushion because duplicate cleanup can mark rows hidden/duplicateOf.
    const constraints: any[] = [orderBy('createdAt', 'desc'), limit(pageSize + 25)];
    if (lastVisible) {
      constraints.push(startAfter(lastVisible));
    }

    const q = query(eventsRef, ...constraints);
    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    const visibleDocs = docs.filter((docItem) => isVisibleEventData(docItem.data()));
    const hasMore = docs.length >= pageSize + 25 || visibleDocs.length > pageSize;
    const pageDocs = visibleDocs.slice(0, pageSize);

    return {
      events: pageDocs.map((docItem) => ({
        ...docItem.data(),
        id: docItem.id,
      } as Event)),
      lastVisible: docs.length > 0 ? docs[docs.length - 1] : lastVisible,
      hasMore,
    };
  },

  async getAllEvents(): Promise<Event[]> {
    const eventsRef = collection(db, 'posts');
    const snapshot = await getDocs(eventsRef);
    return snapshot.docs
      .filter((docItem) => isVisibleEventData(docItem.data()))
      .map((docItem) => ({
        ...docItem.data(),
        id: docItem.id,
      } as Event));
  },

  async createEvent(event: Omit<Event, 'id'>, images: File[]): Promise<string> {
    console.log('Creating event with images:', images.length);
    
    // Create event document
    const docRef = await addDoc(collection(db, 'posts'), {
      ...event,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      Image_gallery_url: []
    });

    console.log(`Event document created with ID: ${docRef.id}`);

    // Upload images
    const Image_gallery_url = await Promise.all(
      images.map(async (image, index) => {
        const imagePath = `${STORAGE_PATH}/${docRef.id}/${image.name}`;
        console.log(`Uploading image ${index + 1}/${images.length} to path: ${imagePath}`);
        
        const storageRef = ref(storage, imagePath);
        await uploadBytes(storageRef, image);
        const url = await getDownloadURL(storageRef);
        console.log(`Successfully uploaded image ${index + 1} to: ${url}`);
        return url;
      })
    );

    // Update document with image URLs
    await updateDoc(docRef, { Image_gallery_url });
    console.log('Updated event document with image URLs:', Image_gallery_url);
    
    return docRef.id;
  },

  async updateEvent(id: string, updates: Partial<Event>): Promise<void> {
    console.log('Updating event with ID:', id); // Debug
    console.log('Updates:', updates); // Debug
    
    if (!id || typeof id !== 'string') {
      throw new Error(`Invalid ID: ${id}`);
    }

    const eventRef = doc(db, 'posts', id.toString());
    try {
      const updateData = {
        ...Object.fromEntries(
          Object.entries(updates).filter(([_, value]) => value !== undefined)
        ),
        updatedAt: new Date().toISOString()
      };
      await updateDoc(eventRef, updateData);
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },

  async deleteEvent(id: string): Promise<void> {
    console.log('=== Starting event deletion process ===');
    console.log(`Event ID: ${id}`);
    
    try {
      // Delete all files in the storage folder
      console.log(`Attempting to delete storage folder contents...`);
      const storagePath = `${STORAGE_PATH}/${id}`;
      console.log(`Storage path: ${storagePath}`);
      
      try {
        const folderRef = ref(storage, storagePath);
        // List all items in the folder
        const listResult = await listAll(folderRef);
        console.log(`Found ${listResult.items.length} files to delete`);

        // Delete all files in parallel
        if (listResult.items.length > 0) {
          const deletePromises = listResult.items.map(async (item) => {
            console.log(`Deleting file: ${item.fullPath}`);
            try {
              await deleteObject(item);
              console.log(`Successfully deleted file: ${item.fullPath}`);
            } catch (fileError) {
              console.error(`Error deleting file ${item.fullPath}:`, fileError);
            }
          });

          await Promise.all(deletePromises);
          console.log('All files deleted successfully');
        } else {
          console.log('No files found in the folder');
        }
      } catch (storageError) {
        console.error('Error with storage operations:', storageError);
        if (storageError.code === 'storage/unauthorized') {
          console.error('Unauthorized access to storage. Check Firebase Storage rules.');
        }
      }

      // Delete the document
      console.log('Deleting event document from Firestore...');
      await deleteDoc(doc(db, 'posts', id));
      console.log('Successfully deleted event document');
      
      console.log('=== Event deletion process completed ===');
    } catch (error) {
      console.error('=== Error in deletion process ===');
      console.error('Error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Stack trace:', error.stack);
      throw error;
    }
  },

  async getNearbyEvents(location: GeoPoint, startTime: Timestamp): Promise<Event[]> {
    try {
      // Query events within a time window (e.g., ±2 hours from the event time)
      const startWindow = new Date((startTime.seconds - 7200) * 1000); // 2 hours before
      const endWindow = new Date((startTime.seconds + 7200) * 1000);   // 2 hours after
      
      const eventsRef = collection(db, 'posts');
      const q = query(
        eventsRef,
        where('startDateTime', '>=', Timestamp.fromDate(startWindow)),
        where('startDateTime', '<=', Timestamp.fromDate(endWindow))
      );

      const snapshot = await getDocs(q);
      return snapshot.docs
        .filter((docItem) => isVisibleEventData(docItem.data()))
        .map(doc => ({
          ...doc.data(),
          id: doc.id
        } as Event));

    } catch (error) {
      console.error('Error getting nearby events:', error);
      return [];
    }
  },

  async getEventsInRadius(center: GeoPoint, radiusInMeters: number): Promise<Event[]> {
    // Calculate the bounding box for the radius
    const lat = center.latitude;
    const lon = center.longitude;
    const r = radiusInMeters / 111300; // convert to degrees

    const box = {
      min: new GeoPoint(lat - r, lon - r),
      max: new GeoPoint(lat + r, lon + r)
    };

    const eventsRef = collection(db, 'posts');
    const q = query(
      eventsRef,
      where('lat_long', '>=', box.min),
      where('lat_long', '<=', box.max)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .filter((docItem) => isVisibleEventData(docItem.data()))
      .map(doc => ({ id: doc.id, ...doc.data() } as Event))
      .filter(event => {
        // More precise circle filtering
        const distance = getDistanceFromLatLonInMeters(
          center.latitude,
          center.longitude,
          event.lat_long.latitude,
          event.lat_long.longitude
        );
        return distance <= radiusInMeters;
      });
  },

  async getAllHosts(): Promise<{ id: string; name: string; }[]> {
    try {
      const hostsCollection = collection(db, 'Hosts');
      const hostsSnapshot = await getDocs(hostsCollection);
      return hostsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().title
      }));
    } catch (error) {
      console.error('Error fetching hosts:', error);
      return [];
    }
  }
};

// Helper function to calculate distance between two points
const getDistanceFromLatLonInMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371000; // Radius of the earth in meters
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const deg2rad = (deg: number) => {
  return deg * (Math.PI/180);
};

export default eventService;